/**
 * Server state for the notification surfaces (#48-#50).
 *
 * Two readers over one query root. The homescreen strip and the bell badge share a single
 * `filter=unread` page — one fetch feeds both, and `unreadTotal` comes from the same response, so
 * the badge can never disagree with the strip. The `/notifications` screen runs an infinite query
 * keyed on its filter, which is what makes switching All ↔ Unread restart at page one instead of
 * replaying a cursor cut under the other list.
 *
 * SSE `notification.fired` invalidates the whole root, so a fire during a session repaints both.
 */

import { useCallback, useState } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  DEFAULT_NOTIFICATIONS_LIMIT,
  type NotificationFilter,
  type NotificationsResponse,
} from '@lw/shared';
import {
  getNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from '../../api/notifications';
import { QK } from '../../api/queryKeys';
import { describeError } from '../../api/client';
import { useToast } from '../../components';

/** Cap of the homescreen strip; past it the track ends with a "View all N" tile (SPEC § strip). */
export const VIRAL_STRIP_LIMIT = 20;

/** The strip + the bell badge. */
export function useUnreadNotifications(): UseQueryResult<NotificationsResponse, Error> {
  return useQuery({
    queryKey: QK.notificationsList('unread', VIRAL_STRIP_LIMIT),
    queryFn: ({ signal }) =>
      getNotifications({ filter: 'unread', limit: VIRAL_STRIP_LIMIT }, signal),
    staleTime: 30_000,
  });
}

/** The `/notifications` screen. */
export function useNotificationsPage(filter: NotificationFilter) {
  return useInfiniteQuery({
    queryKey: QK.notificationsList(filter),
    queryFn: ({ pageParam, signal }) =>
      getNotifications(
        { filter, cursor: pageParam ?? undefined, limit: DEFAULT_NOTIFICATIONS_LIMIT },
        signal,
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });
}

export type NotificationActions = {
  /** Ids currently animating out — the card renders collapsed while its request is in flight. */
  dismissing: ReadonlySet<number>;
  markRead: (id: number) => void;
  markAllRead: () => void;
  markingAll: boolean;
};

/**
 * Mark-as-read for both surfaces. Optimistic in the sense the spec asks for: the card animates out
 * immediately and only the *failure* path is visible — a rejected mutation drops the id from the
 * dismissing set (restoring the card) and reports through the toast layer.
 */
export function useNotificationActions(): NotificationActions {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [dismissing, setDismissing] = useState<ReadonlySet<number>>(new Set());

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: QK.notifications });
  }, [queryClient]);

  const restore = useCallback((id: number) => {
    setDismissing((current) => {
      const next = new Set(current);
      next.delete(id);
      return next;
    });
  }, []);

  const read = useMutation({
    mutationFn: (id: number) => markNotificationRead(id),
    onSuccess: invalidate,
    onError: (error, id) => {
      restore(id);
      toast.show({ tone: 'error', message: describeError(error) });
    },
  });

  const readAll = useMutation({
    mutationFn: () => markAllNotificationsRead(),
    onSuccess: invalidate,
    onError: (error) => {
      setDismissing(new Set());
      toast.show({ tone: 'error', message: describeError(error) });
    },
  });

  const markRead = useCallback(
    (id: number) => {
      setDismissing((current) => new Set(current).add(id));
      read.mutate(id);
    },
    [read],
  );

  const markAllRead = useCallback(() => {
    readAll.mutate();
  }, [readAll]);

  return { dismissing, markRead, markAllRead, markingAll: readAll.isPending };
}
